import { isDefined } from '@e2m-customer-portal/shared';

/**
 * For a given list of names, combines them into the most compact form possible by omitting repeating words at the beginning.
 * @param {Array<string>} names An array of names that should be compacted as much as possible
 * @returns {string} The compacted name with no repeated words or an empty string if the `names` array was empty.
 *
 * @example
 * const iGotFamily = mostCompactName('The Fast', 'The Furious');
 * // iGotFamily === 'The Fast & Furious'
 */
export function mostCompactName(...names: Array<string | undefined>): string {
  if (!names.length) return '';

  const filteredNames = names.filter(isDefined).filter((name) => name?.length);
  if (filteredNames.length === 1) return filteredNames[0];

  let combinedName = filteredNames[0];
  let earliestDifference = Infinity;
  for (let nameIndex = 1; nameIndex < filteredNames.length; nameIndex++) {
    const name = filteredNames[nameIndex];
    for (let characterIndex = 0; characterIndex < name.length; characterIndex++) {
      if (combinedName[characterIndex] !== name[characterIndex]) {
        const difference = combinedName.includes(' ') ? combinedName.substring(0, characterIndex).lastIndexOf(' ') : 0;
        if (difference < earliestDifference) earliestDifference = difference;
        // Abort early if there’s been a mismatch
        if (!earliestDifference) break;
      }
    }
  }

  for (let index = 1; index < filteredNames.length; index++) {
    combinedName = `${combinedName}${index < filteredNames.length - 1 ? ',' : ' &'} ${filteredNames[index].substring(earliestDifference).trim()}`;
  }

  return combinedName;
}
